import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TableConfig } from '@shared/interfaces/table/table-config';
import {
  ConnectionPointSearchQuery,
  ConnectionPointSearchResult,
  ConnectionPointService,
} from '@app/api/nts';
import { CardComponent } from '@shared/components/card/card.component';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { TranslatePipe } from '@ngx-translate/core';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TableComponent } from '@shared/components/table/table.component';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { ModalService } from '@app/core/services/modal.service';
import { AlertService } from '@app/core/services/alert.service';
import { SortParam } from '@app/core/constants/sort-param';
import { catchError, map, of } from 'rxjs';

@Component({
  selector: 'app-connectionpoint-search',
  imports: [
    CardComponent,
    ReactiveFormsModule,
    InputGroupComponent,
    TranslatePipe,
    InputDirective,
    LabelDirective,
    TableComponent,
    ButtonDirective
  ],
  templateUrl: './connectionpoint-search.component.html',
  styleUrl: './connectionpoint-search.component.css'
})
export class ConnectionpointSearchComponent {
  @ViewChild('table') private readonly tableRef?: TableComponent<ConnectionPointSearchResult>;

  public searchForm: FormGroup = new FormGroup({
    connectionPointIdentification: new FormControl(),
    meterNumber: new FormControl(),
    alternativeMeterNumber: new FormControl(),
    streetName: new FormControl(),
    streetNumber: new FormControl(),
    postalCode: new FormControl(),
  });

  private searchParams: ConnectionPointSearchQuery = {};
  private hasSearched = false;

  public connectionPointTable: TableConfig<ConnectionPointSearchResult> = {
    id: 'modal-connectionpoint-search',
    selectable: true,
    selectableType: 'single',
    pageSize: 25,
    columns: [
      {
        title: 'connectionpoint.identification',
        key: 'connectionPointIdentification',
      },
      {
        title: 'meter.meterNumber',
        key: 'meterNumber'
      },
      {
        title: 'address.title',
        key: 'address'
      }
    ],
    serviceCallback: (pageSize: number, page: number, sort?: SortParam) => {
      if (!this.hasSearched) return of({ results: [], totalHitCount: 0 });
      return this.connectionPointService.connectionPointSearchPost(this.searchParams, page, pageSize).pipe(
        map(result => {
          if (result.results && result.results.length >= pageSize) {
            this.alertService.open('modal', 'warning', 'connectionpoint.search.warningLimit');
          }
          return result ?? { results: [], totalHitCount: 0 };
        }),
        catchError(() => of({ results: [], totalHitCount: 0 }))
      );
    },
  };

  public selectedElements: ConnectionPointSearchResult[] = [];

  constructor(
    private readonly modalService: ModalService,
    private readonly connectionPointService: ConnectionPointService,
    private readonly alertService: AlertService,
  ) {}

  public search(): void {
    if (!this.searchForm.valid) return;
    this.searchParams = this.searchForm.value;
    this.hasSearched = true;
    this.tableRef?.search();
  }

  public submit(): void {
    if (this.selectedElements.length === 0) {
      this.alertService.open('modal', 'danger', 'connectionpoint.search.selectWarning');
      return;
    }
    this.modalService.close(this.selectedElements[0]);
  }
}
